import { useEffect } from 'react';
import styled from '@emotion/styled';
import { useAtom, useSetAtom } from 'jotai';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  useGetAnalysisStructureLocationsQuery,
  useGetStructureDataQuery,
  usePaintRegionGroupsQuery,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { useLocalStorage } from '@/hooks';
import { LocationsData } from '@/types';
import { sizeOfAnObjectInMb } from '@/utils/sizeOfAnObject';
import { ErrorLogger } from '@/utils/error-logger';
import {
  INSIGHTS_DATA_LOADER_LOCATION_ERROR,
  INSIGHTS_DATA_LOADER_RISK_ASSESSMENT_ERROR,
} from '@/constants/Errors';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

export const InsightsDataLoader = ({ ...props }) => {
  const structureId = useGetStructureId();
  const [structureLocations, setStructureLocations] = useAtom(state.structureLocations);
  const setHasRiskAssesment = useSetAtom(state.hasRiskAssessment);
  const setStructureBlisterHeightType = useSetAtom(state.structureBlisterHeightType);
  const setCustomBlocksEnabled = useSetAtom(state.customBlocksEnabled);
  const setCustomBlocksNames = useSetAtom(state.customBlocksNames);
  const [locations, setLocations] = useLocalStorage<LocationsData | undefined>(
    `locations`,
    undefined
  );

  usePaintRegionGroupsQuery({
    variables: {
      input: { structureId },
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    onCompleted: (response) => {
      // if paint region groups are found, enable custom blocks
      const paintGroupsExist = response?.paintRegionGroups.length;
      if (paintGroupsExist) {
        setCustomBlocksEnabled(true);
        setCustomBlocksNames(response?.paintRegionGroups?.map((item) => item.name) ?? []);
      }
    },
  });

  const { data, error: locationsError } = useGetAnalysisStructureLocationsQuery({
    variables: {
      structureId,
    },
    skip: !!locations?.[structureId],
  });

  const { data: structureQueryResult, error: structureDataError } = useGetStructureDataQuery({
    variables: {
      structureId,
    },
  });

  useEffect(() => {
    if (locationsError) {
      ErrorLogger.log(
        INSIGHTS_DATA_LOADER_LOCATION_ERROR.withContext({ structureId }).withError(locationsError)
      );
    }

    if (data?.allLocations) {
      if (sizeOfAnObjectInMb(data.allLocations) <= 2.5) {
        setLocations({
          [structureId]: data.allLocations,
        });
        return;
      }
      setStructureLocations(data.allLocations);
    }
  }, [
    data?.allLocations,
    setLocations,
    structureId,
    setStructureLocations,
    structureLocations,
    locationsError,
  ]);

  useEffect(() => {
    if (locations?.[structureId] && !structureLocations)
      setStructureLocations(locations?.[structureId]);
  }, [locations, setStructureLocations, structureId, structureLocations]);

  useEffect(() => {
    if (structureDataError) {
      ErrorLogger.log(
        INSIGHTS_DATA_LOADER_RISK_ASSESSMENT_ERROR.withContext({ structureId }).withError(
          structureDataError
        )
      );
    }
    setHasRiskAssesment(structureQueryResult?.structure?.hasRiskAssessment ?? false);
    setStructureBlisterHeightType(structureQueryResult?.structure?.blisterHeightType ?? undefined);
  }, [
    setHasRiskAssesment,
    structureQueryResult?.structure?.hasRiskAssessment,
    structureId,
    structureDataError,
    structureQueryResult?.structure?.blisterHeightType,
    setStructureBlisterHeightType,
  ]);

  return <Wrapper {...props} />;
};
